import BackspaceIcon from '@mui/icons-material/BackspaceOutlined';
import { Box, Button, IconButton } from '@mui/material';
import { FC, useEffect } from 'react';
import theme from 'App.theme';
import { useLanguage } from 'context/AppContext';

export const PIN_LENGTH = 4;

type Props = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

// on-screen keypad (touch workstations) that also accepts keyboard digits/backspace
const PinPad: FC<Props> = ({ value, onChange, disabled }) => {
  const [lang] = useLanguage();

  useEffect(() => {
    if (disabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (/^\d$/.test(event.key) && value.length < PIN_LENGTH) onChange(value + event.key);
      else if (event.key === 'Backspace') onChange(value.slice(0, -1));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [value, onChange, disabled]);

  const press = (digit: string) => value.length < PIN_LENGTH && onChange(value + digit);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.layout.spacing.medium }}>
      <Box sx={{ display: 'flex', gap: theme.layout.spacing.small }} aria-label="PIN" role="img">
        {Array.from({ length: PIN_LENGTH }, (_, i) => (
          <Box
            key={i}
            sx={{
              width: 16,
              height: 16,
              borderRadius: '50%',
              border: `2px solid ${theme.colors.active}`,
              backgroundColor: i < value.length ? theme.colors.active : 'transparent',
            }}
          />
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 64px)', gap: theme.layout.spacing.small }}>
        {DIGITS.map(digit => (
          <Button key={digit} variant="outlined" disabled={disabled} onClick={() => press(digit)}>
            {digit}
          </Button>
        ))}
        <span />
        <Button variant="outlined" disabled={disabled} onClick={() => press('0')}>
          0
        </Button>
        <IconButton
          aria-label={lang.PinLogin.keypad.delete}
          disabled={disabled || !value}
          onClick={() => onChange(value.slice(0, -1))}
        >
          <BackspaceIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default PinPad;
